/**
 * Cookie consent state: the gate everything analytics-related sits behind.
 *
 * UK site (PECR): analytics is OFF until the visitor opts in, and "reject" is a first-class choice,
 * not a dismiss. Persisted to localStorage. If storage is unavailable the choice holds for the
 * session only and the banner returns next load: the safe failure is "no consent".
 *
 * There is one optional category. The site sets no other non-essential cookies, so there is nothing
 * else to ask about.
 */

export interface ConsentChoices {
    analytics: boolean;
}

export const NO_CONSENT: ConsentChoices = { analytics: false };

export const CONSENT_STORAGE_KEY = 'self-consent';

/** Bump when what analytics covers changes, so returning visitors are asked again. */
const CONSENT_VERSION = 1;

export interface StoredConsent {
    /** False until the visitor has answered the banner. */
    decided: boolean;
    choices: ConsentChoices;
}

export const readStoredConsent = (): StoredConsent => {
    try {
        const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
        if (!raw) return { decided: false, choices: NO_CONSENT };

        const parsed = JSON.parse(raw) as { v?: number; choices?: Partial<ConsentChoices> };
        if (parsed.v !== CONSENT_VERSION || !parsed.choices) return { decided: false, choices: NO_CONSENT };

        return { decided: true, choices: { analytics: parsed.choices.analytics === true } };
    } catch {
        return { decided: false, choices: NO_CONSENT };
    }
};

export const writeStoredConsent = (choices: ConsentChoices): void => {
    try {
        window.localStorage.setItem(
            CONSENT_STORAGE_KEY,
            JSON.stringify({ v: CONSENT_VERSION, at: new Date().toISOString(), choices }),
        );
    } catch {
        /* storage unavailable: the choice holds for this session only */
    }
};
